import React, { useState, useEffect } from 'react'
import { compose } from 'redux'
import { withRouter } from 'react-router'
import { Form } from 'react-bootstrap'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import StripeForm from '../StripeForm'
import FA from '../../../utils/FA'
import NachaMessage from '../NachaMessage'
import ConvenienceFeeCheckbox from './ConvenienceFeeCheckbox'
import rollbar from 'utils/rollbarLogger'
import { paymentTypes } from './PaymentSelector'
import PaymentProfileOptions from '../detailInputs/PaymentProfileOptions'
import PaymentTestModeWarning from 'components/payments/paymentInputs/PaymentTestModeWarning'
import Placeholder from '../../Placeholder'
import { captureFunctions, paymentsEnabled, usesIntents } from 'components/payments/stripe/stripeUtils'
import { filterValues } from 'utils'
import duckify, { singleDuck } from '../../../utils/duckify'
import { billingConfigs, customers, users } from '../../../ducks'
import { apiFetch } from '../../../utils'

const OnlinePaymentInput = props => {
  const { allDucksReady, customer, paymentMethods } = props
  const [stripePromise, setStripePromise] = useState(null)
  const [savePaymentProfile, setSavePaymentProfile] = useState(true)
  const [paymentMethodTypes, setPaymentMethodTypes] = useState([])
  const [clientSecret, setClientSecret] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [firstRun, setFirstRun] = useState(true)

  useEffect(() => {
    if (allDucksReady && customer?.stripe_api_key) {
      setStripePromise(loadStripe(customer.stripe_api_key))
    }
  }, [allDucksReady])

  useEffect(() => {
    const availableMethods = []

    if (customer?.payment_config.payment_methods['credit card'] && paymentMethods?.includes(paymentTypes.CREDIT_CARD)) {
      availableMethods.push('card')
    }
    if (customer?.payment_config.payment_methods['electronic check'] && paymentMethods?.includes(paymentTypes.ACH)) {
      if (customer?.currency.toLowerCase() === 'usd') {
        availableMethods.push('us_bank_account')
      }
      else if (customer?.currency.toLowerCase() === 'cad') {
        availableMethods.push('acss_debit')
      }
    }

    setPaymentMethodTypes(availableMethods)

    if (!global.PAYMENTS_TEST_MODE && firstRun) {
      setLoading(true)
      setFirstRun(false)
      apiFetch.post({}, 'stripe_custom/setup_intent', {
        customer_id: customer.id,
        payment_method_types: availableMethods,
      })
        .then(data => {
          setClientSecret(data.client_secret)
          setLoading(false)
        })
        .catch(error => {
          rollbar.error('Error fetching SetupIntent for payment:', error)
          setError(error)
        });
    }
  }, [savePaymentProfile, customer?.payment_config])

  const appearanceConfig = {
    theme: 'stripe',
    labels: 'above',
    variables: {
      colorText: '#494542',
    }
  };

  const options = !global.PAYMENTS_TEST_MODE ?
    {
      onBehalfOf: customer?.cc_payment_info?.stripe_account_id,
      currency: customer?.currency?.toLowerCase(),
      clientSecret,
      appearance: appearanceConfig,
    }
    : {
      mode: savePaymentProfile ? 'setup' : 'payment',
      amount: savePaymentProfile ? 0 : 100,   // This amount is just a place holder.  Actual amount gets added later.  Some payment methods like 'affirm' need a min $ ie $50
      onBehalfOf: customer?.cc_payment_info?.stripe_account_id,
      currency: customer?.currency?.toLowerCase(),
      paymentMethodTypes,
      appearance: appearanceConfig,
    }

  return (
    <Placeholder.loading ready={allDucksReady && !loading}>
      {error && <div className="alert alert-danger">{error}</div>}
      <Elements stripe={stripePromise} options={options}>
        <PaymentForm
          {...props}
          paymentMethodTypes={paymentMethodTypes}
          savePaymentProfile={savePaymentProfile}
          setSavePaymentProfile={setSavePaymentProfile} />
      </Elements>
    </Placeholder.loading>
  )
}

const PaymentForm = ({
  billingConfig,
  customer,
  amount,
  paymentDetails,
  showSaveProfileOption,
  showAutopayOption,
  beforeSubmit,
  onSubmit,
  afterSubmit,
  paymentDetailsChanged,
  paymentMethod,
  paymentMethodTypes,
  disabled = false,
  savePaymentProfile,
  selectPaymentMethod,
  setSavePaymentProfile,
  user,
  ...props
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const buttonText = props.buttonText(customer, 'online payment')
  const submittingText = props.submittingText(customer, 'online payment')

  const [submitting, setSubmitting] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState(null)
  const [cardComplete, setCardComplete] = useState(false)
  const [autopay, setAutopay] = useState(true)
  const [convenienceFeeAccepted, setConvenienceFeeAccepted] = useState(false)
  const [confirmationToken, setConfirmationToken] = useState(null)
  const [readyToConfirm, setReadyToConfirm] = useState(false)
  const [selectedPaymentType, setSelectedPaymentType] = useState(null)

  // Overriding order for application fees and deposits.
  // This can be removed if application fee and deposits pages are created in react.
  const paymentMethodOrder = paymentMethod === paymentTypes.ACH ? [
    customer?.currency.toLowerCase() === 'usd' ? 'us_bank_account' : 'acss_debit', 'card'
  ] : []

  const walletOption = user?.impersonate ? 'never' : 'auto';
  const options = {
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: false,
      spacedAccordionItems: true,
    },
    wallets: {
      applePay: walletOption,
      googlePay: walletOption,
      link: walletOption
    },
    // Removing name from rendering twice through PaymentElement.  If we remove our name input field remove this
    fields: {
      billingDetails: {
        name: 'never'
      }
    },
    paymentMethodOrder: paymentMethodOrder,
  }

  const validate = () => {
    const convenienceFeeOk = convenienceFeeAccepted || !convenienceFeeConfirmationRequired()
    return paymentDetails?.valid && cardComplete && convenienceFeeOk
  }

  const convenienceFeeConfirmationRequired = () => {
    const { payment_methods } = customer?.payment_config || {}
    const { show_confirmation_message = false } = payment_methods?.[paymentMethod]?.convenience_fee || {}
    return show_confirmation_message && customer?.convenienceFee !== 0
  }

  const handleError = (error) => {
    const message = error.message || error.error || 'There was an error processing your payment.'
    rollbar.error(`Error processing your payment: ${message}`, error)
    setError(`Error: ${message}`)
    setSubmitting(false)
  }

  const beginSubmit = () => {
    if (submitting) {
      throw new Error('duplicate submission')
    } else {
      setSubmitting(true)
      setError(null)
    }
  }

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      handleError({ message: 'Stripe.js has not loaded' });
      return;
    }

    if (submitting) {
      throw new Error('Duplicate submission');
    }

    const valid = validate();
    if (!valid) {
      handleError({ message: 'Invalid payment details' });
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await beforeSubmit();

      if (global.PAYMENTS_TEST_MODE) {
        const token = "mock_confirmation_token_12345";

        setSelectedPaymentType('card');
        setConfirmationToken(token);
        setReadyToConfirm(true);

        await handleConfirm(token);

        return;
      }

      await elements.submit();

      const result = await stripe.createConfirmationToken({
        elements,
        paymentDetails,
        params: {
          payment_method_data: {
            billing_details: {
              name: paymentDetails.name,
            },
          },
        },
      });

      if (result.error) {
        throw result.error;
      }

      setConfirmationToken(result.confirmationToken);
      setReadyToConfirm(true);

      if (!['google_pay', 'apple_pay'].includes(selectedPaymentType)) {
        await handleConfirm(result.confirmationToken.id);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (token) => {
    try {
      setConfirming(true)

      const extra = usesIntents(customer)
        ? {
          ...captureFunctions({
            paymentDetails,
            confirmSetup: stripe.confirmSetup,
            confirmPayment: stripe.confirmPayment,
            elements,
            paymentElement: true,
            setupDetails: {
              autopay,
              payment_method_types: paymentMethodTypes,
              payment_method: paymentMethod,
            },
          }),
          options: {
            stripe_save_profile: savePaymentProfile,
          },
        }
        : {}

      const submission = await onSubmit({
        savePaymentProfile,
        token,
        payment_method: paymentMethod || paymentTypes.CREDIT_CARD,
        paymentDetails,
        autopay,
        usesPaymentElement: true,
        ...extra,
      })

      await afterSubmit(submission)
    } catch (err) {
      handleError(err)
    } finally {
      setConfirming(false)
    }
  }

  if (!paymentsEnabled(customer.cc_payment_gateway, customer.cc_payment_info)) {
    return (
      <div className="alert alert-danger">
        <FA name="warning" nbsp />
        Online payments have not been configured. Please contact the system administrator.
      </div>
    )
  }

  const valid = validate()
  const isDisabled = disabled || !valid || readyToConfirm

  const handlePaymentChange = type => {
    setSelectedPaymentType(type)

    const paymentMethodMap = {
      card: paymentTypes.CREDIT_CARD,
      us_bank_account: paymentTypes.ACH,
      acss_debit: paymentTypes.ACH,
      google_pay: paymentTypes.CREDIT_CARD,
      apple_pay: paymentTypes.CREDIT_CARD,
    }

    // Prevent an endless loop on mount
    if (paymentMethodMap[type] !== paymentMethod) {
      selectPaymentMethod(paymentMethodMap[type], null)
    }
  }

  return (
    <StripeForm
      stripe={stripe}
      elements={elements}
      beforeSubmit={beginSubmit}
      onError={handleError}
      details={filterValues(paymentDetails)}
      usesIntents={usesIntents(customer)}
    >
      {error && <div className="alert alert-danger">{error}</div>}

      <PaymentTestModeWarning testMode={customer.cc_payment_info?.test_mode} />

      <fieldset className="payment-online-payment-input">

        <Placeholder.loading ready={!!stripe}>
          <div className="StripeElement">
            <Form.Label className='payment-name-label'>
              Name
            </Form.Label>
            <Form.Control
              type='text'
              id='name'
              name='name'
              onChange={e => paymentDetailsChanged({ valid: !!e.target.value, name: e.target.value })}
              placeholder='Full Name of Account Holder'
              disabled={submitting}
            />
          </div>
          {global.PAYMENTS_TEST_MODE ?
            <React.Fragment>
              <label className="payment-card-field">
                <span className="payment-input-label">{paymentMethod === paymentTypes.CREDIT_CARD ? 'Card:' : 'Email:'}</span>
                {paymentMethod === paymentTypes.CREDIT_CARD ?
                  <input
                    type="text"
                    className="payment-input-field"
                    name='bogus-card-number'
                    disabled={submitting}
                    onChange={event => setCardComplete(true)}
                    placeholder='The system is in test mode.  This is not a real credit card number field.'
                  /> :
                  <input
                    type="text"
                    className="payment-input-field"
                    name='email'
                    disabled={submitting}
                    onChange={event => setCardComplete(true)}
                    placeholder='The system is in test mode.  This is not a real email field.'
                  />
                }
              </label>
            </React.Fragment>
            :

            <div className={'stripe-payment-element'}>
              <PaymentElement
                options={options}
                onChange={event => {
                  handlePaymentChange(event.value.type)
                  setCardComplete(event.complete)
                  setError(event.error ? event.error.message : null)
                }}
                disabled={submitting}
              />
            </div>
          }
        </Placeholder.loading>
      </fieldset>

      {convenienceFeeConfirmationRequired() && (
        <ConvenienceFeeCheckbox
          customer={customer}
          checked={convenienceFeeAccepted}
          paymentMethod={paymentMethod || paymentTypes.CREDIT_CARD}
          onChange={() => setConvenienceFeeAccepted(!convenienceFeeAccepted)}
          disabled={submitting}
        />
      )}

      <PaymentProfileOptions
        billingConfig={billingConfig}
        customer={customer}
        showSaveProfileOption={showSaveProfileOption}
        showAutopayOption={showAutopayOption}
        savePaymentProfile={savePaymentProfile}
        paymentMethod={paymentMethod}
        autopay={autopay}
        onChange={({ savePaymentProfile, autopay }) => {
          setSavePaymentProfile(savePaymentProfile)
          setAutopay(autopay)
        }}
        disabled={submitting}
      />

      <div className="sa-form-submit">
        <div className="sa-form-submit-button">
          {submitting ? (
            <button className="btn btn-primary col-24" disabled>
              <FA name="spinner" spin nbsp />
              {submittingText}
            </button>
          ) : (
            <button className="btn btn-primary col-24" onClick={handleSubmit} disabled={isDisabled}>
              {buttonText}
            </button>
          )}
        </div>

        {['google_pay', 'apple_pay'].includes(selectedPaymentType) && readyToConfirm && (
          <button className="btn btn-success mt-3" onClick={() => handleConfirm(confirmationToken)}
                  disabled={confirming}>
            {confirming ? (
              <>
                <FA name="spinner" spin/> Confirming...
              </>
            ) : (
              'Confirm Payment Info'
            )}
          </button>
        )}

        <NachaMessage amount={amount} buttonText={buttonText} customer={customer} />
      </div>
    </StripeForm>
  )
}

const mapDucksToProps = () => ({
  billingConfig: singleDuck(billingConfigs, 'current'),
  user: singleDuck(users, 'current'),
  customer: singleDuck(customers, 'current'),
})

export default compose(
  withRouter,
  duckify(mapDucksToProps, () => ({}), () => ({}))
)(OnlinePaymentInput)
